import AddLinkIcon from '@mui/icons-material/AddLink';
import {Alert, Box, Button, CircularProgress, Dialog, DialogContent, MenuItem, Stack, TextField} from '@mui/material';
import {ProfileListItem, ProfileListStay} from 'crqlar-react-common/dist/models/profile/profileListItem.model';
import {Reservation} from 'crqlar-react-common/dist/models/reservation/reservation.model';
import {Survey, SurveyStatus} from 'crqlar-react-common/dist/models/survey/survey.model';
import {ProfileAggregationService} from 'crqlar-react-common/dist/services/aggregation/profileAggregation.service';
import {ProfileService} from 'crqlar-react-common/dist/services/profile/profile.service';
import {SurveyService} from 'crqlar-react-common/dist/services/survey/survey.service';
import {useReadOnly} from 'crqlar-react-common/dist/utils/hooks/users/useReadOnly';
import {getNameForLanguage} from 'crqlar-react-common/dist/utils/translations.util';
import {ReactNode, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useQuery, useQueryClient} from 'react-query';
import SurveyInvitationDetails, {
    invitationGuestEmailLine,
    invitationGuestName,
    SurveyInvitationGuest
} from './SurveyInvitationDetails';
import SurveyInviteHeader from './SurveyInviteHeader';
import SurveyInviteSearch, {SURVEY_INVITE_SEARCH_QUERY_KEY} from './SurveyInviteSearch';
import SurveyInviteSurveyField from './SurveyInviteSurveyField';
import SurveyInviteTestStep from './SurveyInviteTestStep';
import {SHARE_DIALOG_MAX_WIDTH} from './surveyShare.constants';

interface Props {
    open: boolean
    onClose: () => void
    /** From the survey's own page; otherwise the details offer the guest surveys. */
    survey?: Survey
    /** From a reservation: the stay is fixed, the guest is its primary unless `profileId` names another. */
    reservation?: Reservation
    /** From a profile, or an agency booking's actual guest. */
    profileId?: string
    /** The room as the caller shows it; the rate snapshot only names it when the PMS sent one. */
    roomName?: string
}

/** Where an invite that starts from nobody is: searching, a picked guest, a typed email, or a test. */
type Step =
    | {kind: 'search'}
    | {kind: 'guest', guest: ProfileListItem, survey?: Survey}
    | {kind: 'contact', email: string}
    | {kind: 'test'};

/**
 * The one way staff invite someone to a survey by hand, from a survey's page, a reservation, the daily list or a
 * profile. An entry point that knows the guest opens on their details; otherwise a search finds a guest or takes any
 * email address (a new contact), and Send test mails the survey to any address without saving answers.
 */
export default function SurveyInviteDialog({open, onClose, survey, reservation, profileId, roomName}: Props) {
    const knownProfileId = profileId || reservation?.primaryProfileId || reservation?.profileIds?.find(Boolean);
    const known = !!knownProfileId || !!reservation;
    const [step, setStep] = useState<Step>({kind: 'search'});
    const backToSearch = () => setStep({kind: 'search'});

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            aria-label={survey?.name}
            // Pinned to the top on every step: results growing under the search field must never move it.
            sx={{'& .MuiDialog-container': {alignItems: 'flex-start'}}}
            PaperProps={{sx: {maxWidth: SHARE_DIALOG_MAX_WIDTH, mt: '10vh', maxHeight: 'calc(100% - 10vh - 32px)'}}}
        >
            {known ? (
                <KnownGuest
                    profileId={knownProfileId}
                    reservation={reservation}
                    roomName={roomName}
                    survey={survey}
                    onClose={onClose}
                />
            ) : (
                <>
                    {/* Kept mounted while another step shows, so going back finds the search as it was left. */}
                    <Box sx={{display: step.kind === 'search' ? 'contents' : 'none'}}>
                        <SurveyInviteSearch
                            survey={survey}
                            active={step.kind === 'search'}
                            onPickGuest={guest => setStep({kind: 'guest', guest})}
                            onPickEmail={email => setStep({kind: 'contact', email})}
                            onSendTest={() => setStep({kind: 'test'})}
                            onClose={onClose}
                        />
                    </Box>
                    {step.kind === 'guest' && (
                        <GuestDetails
                            key={step.guest.id}
                            guest={step.guest}
                            initialStay={step.guest.selectedStay}
                            survey={survey ?? step.survey}
                            onBack={backToSearch}
                            onClose={onClose}
                        />
                    )}
                    {step.kind === 'contact' && (
                        <NewContactDetails
                            email={step.email}
                            survey={survey}
                            onCreated={(guest, chosen) => setStep({kind: 'guest', guest, survey: chosen})}
                            onBack={backToSearch}
                            onClose={onClose}
                        />
                    )}
                    {step.kind === 'test' && <SurveyInviteTestStep survey={survey} onBack={backToSearch} onClose={onClose}/>}
                </>
            )}
        </Dialog>
    );
}

/**
 * A typed email no guest has: optional name, the survey and a language. Creating the link creates the person as a
 * profile of type Other (API `SurveyInvitationServiceV3.contactFor`), from where they're an ordinary guest.
 */
function NewContactDetails({email, survey: knownSurvey, onCreated, onBack, onClose}: {
    email: string
    survey?: Survey
    onCreated: (guest: ProfileListItem, survey: Survey) => void
    onBack: () => void
    onClose: () => void
}) {
    const {t, i18n} = useTranslation();
    const readOnly = useReadOnly();
    const queryClient = useQueryClient();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [chosenSurvey, setChosenSurvey] = useState<Survey>();
    const [chosenLanguage, setChosenLanguage] = useState<string>();
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    const survey = knownSurvey ?? chosenSurvey;

    const languages = survey?.languages?.filter(Boolean) ?? [];
    // The staff member's own language when the survey offers it, else the survey's first.
    const language = [chosenLanguage, i18n.language?.slice(0, 2)].find(candidate => !!candidate && languages.includes(candidate))
        ?? languages[0];

    const createLink = async () => {
        if (readOnly || busy || !survey?.id) return;
        setBusy(true);
        setFailed(false);
        try {
            const invitation = await SurveyService.invitationCreate({
                surveyId: survey.id,
                email,
                language,
                firstName: firstName.trim() || undefined,
                lastName: lastName.trim() || undefined,
            });
            // The person is a guest now: back in the search, the same address finds them instead of offering "Invite".
            void queryClient.invalidateQueries(SURVEY_INVITE_SEARCH_QUERY_KEY);
            onCreated({
                id: invitation.guestProfileId,
                firstName: firstName.trim() || undefined,
                lastName: lastName.trim() || undefined,
                primaryEmail: email,
            } as ProfileListItem, survey);
        } catch {
            setFailed(true);
            setBusy(false);
        }
    };

    return (
        <>
            <SurveyInviteHeader title={email} subtitle={t('New contact')} onBack={onBack} onClose={onClose}/>
            <DialogContent sx={{px: 2.5, pb: 3, '&&': {pt: 1}}}>
                <Stack gap={2}>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField
                            fullWidth size={'small'} label={t('First name')} value={firstName}
                            onChange={event => setFirstName(event.target.value)}
                        />
                        <TextField
                            fullWidth size={'small'} label={t('Last name')} value={lastName}
                            onChange={event => setLastName(event.target.value)}
                        />
                    </Stack>
                    {!knownSurvey && <SurveyInviteSurveyField value={chosenSurvey} onChange={setChosenSurvey}/>}
                    {languages.length > 1 && (
                        <TextField
                            select fullWidth size={'small'} label={t('Language')} value={language ?? ''}
                            onChange={event => setChosenLanguage(event.target.value)}
                        >
                            {languages.map(code => (
                                <MenuItem key={code} value={code}>{getNameForLanguage(code, true) || code}</MenuItem>
                            ))}
                        </TextField>
                    )}
                    {failed && <Alert severity={'error'}>{t('This link could not be created.')}</Alert>}
                    <Box sx={{pt: 1}}>
                        <Button
                            variant={'contained'}
                            disableElevation
                            startIcon={busy ? <CircularProgress size={16} color={'inherit'}/> : <AddLinkIcon/>}
                            disabled={readOnly || busy || survey?.status !== SurveyStatus.PUBLISHED}
                            onClick={createLink}
                        >
                            {t('Create link')}
                        </Button>
                    </Box>
                </Stack>
            </DialogContent>
        </>
    );
}

/** The guest the entry point names: their name and email are loaded, the stay comes from the reservation. */
function KnownGuest({profileId, reservation, roomName, survey, onClose}: {
    profileId?: string
    reservation?: Reservation
    roomName?: string
    survey?: Survey
    onClose: () => void
}) {
    const {t} = useTranslation();
    const guestQuery = useQuery(
        ['survey-invite-guest', profileId],
        () => ProfileAggregationService.listProfiles({id: [profileId!], followMerge: false, limit: {offset: 0, limit: 1}}),
        {enabled: !!profileId}
    );
    const guest: SurveyInvitationGuest = guestQuery.data?.content?.[0] ?? {id: profileId};
    const stay: ProfileListStay | undefined = reservation && {
        reservationId: reservation.id,
        roomId: roomName || reservation.rateSnaps?.find(rate => rate.resourceName)?.resourceName,
        startDay: reservation.expectedStartDay,
        endDay: reservation.expectedEndDay,
    };

    return (
        <GuestDetails
            guest={guest}
            initialStay={stay}
            stayFixed={!!reservation}
            sectorId={reservation?.sectorId}
            survey={survey}
            loadError={guestQuery.isError ? (
                <Alert severity={'error'} action={<Button onClick={() => void guestQuery.refetch()}>{t('Retry')}</Button>}>
                    {t('Guest could not be loaded.')}
                </Alert>
            ) : undefined}
            onClose={onClose}
        />
    );
}

function GuestDetails({guest, initialStay, stayFixed = false, sectorId, survey: knownSurvey, loadError, onBack, onClose}: {
    guest: SurveyInvitationGuest
    initialStay?: ProfileListStay
    stayFixed?: boolean
    sectorId?: string
    survey?: Survey
    loadError?: ReactNode
    onBack?: () => void
    onClose: () => void
}) {
    const {t} = useTranslation();
    const [chosenStayId, setChosenStayId] = useState<string>();
    const [chosenSurvey, setChosenSurvey] = useState<Survey>();
    const survey = knownSurvey ?? chosenSurvey;

    const profileQuery = useQuery(
        ['survey-invitation-profile', guest.id],
        () => ProfileService.getProfileById(guest.id!, false),
        {enabled: !!guest.id && !stayFixed}
    );
    const profile = profileQuery.data;
    const stays: ProfileListStay[] = Array.from(new Map([
        initialStay,
        ...(profile?.guestDialogStays ?? []),
        ...(profile?.activeStays ?? []),
        ...(profile?.upcomingStays ?? []),
        ...(profile?.pastStays ?? []),
    ].filter((candidate): candidate is ProfileListStay => !!candidate?.reservationId)
        .map(candidate => [candidate.reservationId!, candidate])).values());
    // Without a stay from the entry point, the one the guest is on now, else none.
    const defaultStayId = initialStay?.reservationId ?? profile?.activeStays?.find(candidate => candidate.reservationId)?.reservationId;
    const stayId = chosenStayId ?? defaultStayId ?? '';
    const stay = stays.find(candidate => candidate.reservationId === stayId);

    return (
        <>
            <SurveyInviteHeader
                title={invitationGuestName(guest)}
                subtitle={invitationGuestEmailLine(guest) || undefined}
                onBack={onBack}
                onClose={onClose}
            />
            <DialogContent sx={{px: 2.5, pb: 3, '&&': {pt: 1}}}>
                {loadError}
                {profileQuery.isError && (
                    <Alert severity={'error'} sx={{mb: 2}} action={<Button onClick={() => void profileQuery.refetch()}>{t('Retry')}</Button>}>
                        {t('Other stays could not be loaded.')}
                    </Alert>
                )}
                <SurveyInvitationDetails
                    key={`${guest.id}:${stayId}:${survey?.id}`}
                    guest={guest}
                    stay={stay}
                    stays={stayFixed ? undefined : stays}
                    onStayChange={setChosenStayId}
                    survey={survey}
                    surveyField={knownSurvey ? undefined : (
                        <SurveyInviteSurveyField value={chosenSurvey} onChange={setChosenSurvey} sectorId={sectorId}/>
                    )}
                />
            </DialogContent>
        </>
    );
}
